import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CalendarCheck, AlertCircle, Clock, CalendarDays, CheckCircle2, Circle, Clapperboard, Camera, Scissors, MessageSquare } from 'lucide-react';
import { FaseBadge } from '../lib/produzione';
import api from '../api';
import { PageHeader, Card, Badge, EmptyState, ListSkeleton } from '../components/ui';

const todayStr = () => new Date().toISOString().split('T')[0];

const PRI = { alta: 'red', media: 'yellow', bassa: 'neutral' };

function Row({ icon: Icon, iconColor, title, sub, right, onClick }) {
  return (
    <div onClick={onClick}
      style={{ display:'flex', alignItems:'center', gap:11, padding:'12px 14px', cursor: onClick ? 'pointer' : 'default',
        borderBottom:'1px solid var(--border)', transition:'background 0.1s' }}
      onMouseEnter={e => onClick && (e.currentTarget.style.background='var(--surface-h)')}
      onMouseLeave={e => onClick && (e.currentTarget.style.background='transparent')}>
      <Icon size={15} color={iconColor} style={{ flexShrink:0 }}/>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13.5, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{title}</div>
        {sub && <div style={{ fontSize:11.5, color:'var(--text-dim)', marginTop:1 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

function Section({ title, count, accent, children }) {
  return (
    <div style={{ marginBottom:18 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
        <span style={{ fontSize:11, fontWeight:700, color: accent || 'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>{title}</span>
        <span style={{ fontSize:11, color:'var(--text-dim)' }}>{count}</span>
      </div>
      <Card padding={0} style={{ overflow:'hidden' }}>{children}</Card>
    </div>
  );
}

export default function AgendaOggi() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [scad, setScad] = useState([]);
  const [contenuti, setContenuti] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/task'),
      api.get('/scadenzario', { params: { giorni: 7 } }).catch(() => ({ data: [] })),
      api.get('/ped/miei', { silent: true }).catch(() => ({ data: [] })),
    ]).then(([t, s, c]) => {
      setTasks(Array.isArray(t.data) ? t.data : []);
      setScad(Array.isArray(s.data) ? s.data : []);
      setContenuti(Array.isArray(c.data) ? c.data : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const mine = useMemo(() => {
    const uid = user?.id;
    return tasks.filter(t => t.assegnata_a === uid || t.assegnata_a === String(uid));
  }, [tasks, user]);

  const oggi = todayStr();
  const scadute = mine.filter(t => t.stato !== 'completata' && t.stato !== 'annullata' && t.data_scadenza && t.data_scadenza < oggi);
  const diOggi  = mine.filter(t => t.stato !== 'completata' && t.stato !== 'annullata' && t.data_scadenza === oggi);
  const completateOggi = mine.filter(t => t.stato === 'completata' && t.data_scadenza === oggi);
  const prossimi = scad.filter(s => !s.scaduto && s.data_scadenza !== oggi);

  const toggle = async (t, e) => {
    e.stopPropagation();
    const next = t.stato === 'completata' ? 'da_fare' : 'completata';
    await api.put(`/task/${t.id}`, { stato: next });
    setTasks(ts => ts.map(x => x.id === t.id ? { ...x, stato: next } : x));
  };

  const checkbox = (t) => (
    <button onClick={(e) => toggle(t, e)} title={t.stato === 'completata' ? 'Riapri' : 'Completa'}
      style={{ width:20, height:20, minWidth:20, padding:0, boxSizing:'border-box', borderRadius:'50%', flexShrink:0,
        border: t.stato === 'completata' ? 'none' : '1.5px solid var(--border-h)',
        background: t.stato === 'completata' ? 'var(--green)' : 'transparent',
        cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
      {t.stato === 'completata' && <CheckCircle2 size={12} color="var(--on-accent)"/>}
    </button>
  );

  // Contenuti assegnati a me: cosa richiede un'azione oggi / a breve
  const in7 = (() => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().split('T')[0]; })();
  const dataDi = (c) => {
    const riprese = c.data_riprese ? c.data_riprese.slice(0, 10) : null;
    const date = [riprese, c.scadenza_montaggio, c.data_pubblicazione].filter(Boolean).sort();
    return date[0] || null;
  };
  const mieiContenuti = contenuti
    .filter(c => { const d = dataDi(c); return Number(c.commenti_aperti) > 0 || (d && d <= in7) || c.cliente_appro === 'modifiche'; })
    .sort((a, b) => (dataDi(a) || '9999').localeCompare(dataDi(b) || '9999'));
  const contenutiUrgenti = mieiContenuti.filter(c => (dataDi(c) || '9999') <= oggi || c.cliente_appro === 'modifiche').length;

  const total = scadute.length + diOggi.length + contenutiUrgenti;

  return (
    <div className="eden-page eden-page--narrow">
      <PageHeader
        title="La mia giornata"
        subtitle={new Date().toLocaleDateString('it-IT', { weekday:'long', day:'numeric', month:'long' })}
        icon={CalendarCheck}
        actions={total > 0 ? <Badge tone={scadute.length ? 'red' : 'yellow'}>{total} da fare</Badge> : <Badge tone="green" icon={CheckCircle2}>in pari</Badge>}
      />

      {loading ? (
        <ListSkeleton rows={5} />
      ) : (total === 0 && completateOggi.length === 0 && prossimi.length === 0 && mieiContenuti.length === 0) ? (
        <EmptyState icon={CheckCircle2} title="Tutto sotto controllo"
          hint="Non hai task scadute o in scadenza oggi. Goditi la giornata." />
      ) : (
        <>
          {scadute.length > 0 && (
            <Section title="Scadute" count={`${scadute.length}`} accent="var(--red)">
              {scadute.map(t => (
                <Row key={t.id} icon={AlertCircle} iconColor="var(--red)"
                  title={t.titolo} sub={t.cliente_nome}
                  right={<div style={{ display:'flex', alignItems:'center', gap:8 }}><Badge tone={PRI[t.priorita]}>{t.priorita}</Badge>{checkbox(t)}</div>}
                  onClick={() => navigate(`/task?open=${t.id}`)} />
              ))}
            </Section>
          )}

          {mieiContenuti.length > 0 && (
            <Section title="I miei contenuti — prossimi 7 giorni" count={`${mieiContenuti.length}`} accent="var(--purple)">
              {mieiContenuti.map(c => {
                const ripreseG = c.data_riprese ? c.data_riprese.slice(0, 10) : null;
                const fmt = (d) => d === oggi ? 'oggi' : new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
                const bits = [];
                if (ripreseG && ripreseG <= in7) bits.push(<span key="r" style={{ display:'inline-flex', alignItems:'center', gap:3, color: ripreseG <= oggi ? 'var(--red)' : undefined }}><Camera size={11}/> riprese {fmt(ripreseG)} {new Date(c.data_riprese).toLocaleTimeString('it-IT', { hour:'2-digit', minute:'2-digit' })}</span>);
                if (c.scadenza_montaggio && c.scadenza_montaggio <= in7 && ['script','preproduzione','riprese','montaggio'].includes(c.fase_produzione)) bits.push(<span key="m" style={{ display:'inline-flex', alignItems:'center', gap:3, color: c.scadenza_montaggio <= oggi ? 'var(--red)' : undefined }}><Scissors size={11}/> montaggio {fmt(c.scadenza_montaggio)}</span>);
                if (c.data_pubblicazione && c.data_pubblicazione <= in7) bits.push(<span key="p" style={{ color: c.data_pubblicazione < oggi ? 'var(--red)' : undefined }}>📅 esce {fmt(c.data_pubblicazione)}</span>);
                if (Number(c.commenti_aperti) > 0) bits.push(<span key="c" style={{ display:'inline-flex', alignItems:'center', gap:3, color:'var(--yellow)' }}><MessageSquare size={11}/> {c.commenti_aperti} commenti</span>);
                if (c.cliente_appro === 'modifiche') bits.push(<span key="x" style={{ color:'var(--yellow)', fontWeight:600 }}>il cliente chiede modifiche</span>);
                return (
                  <Row key={c.id} icon={Clapperboard} iconColor="var(--purple)"
                    title={c.titolo_post}
                    sub={<span style={{ display:'flex', gap:10, flexWrap:'wrap' }}><span>{c.cliente_nome} · {c.formato}</span>{bits}</span>}
                    right={c.fase_produzione ? <FaseBadge fase={c.fase_produzione}/> : <Badge>{c.stato}</Badge>}
                    onClick={() => navigate(`/ped/contenuto/${c.id}`)} />
                );
              })}
            </Section>
          )}

          <Section title="Oggi" count={`${diOggi.length}`} accent="var(--yellow)">
            {diOggi.length === 0
              ? <div style={{ padding:'18px 14px', fontSize:13, color:'var(--text-dim)' }}>Nessun task in scadenza oggi.</div>
              : diOggi.map(t => (
                  <Row key={t.id} icon={Circle} iconColor="var(--yellow)"
                    title={t.titolo} sub={t.cliente_nome}
                    right={<div style={{ display:'flex', alignItems:'center', gap:8 }}><Badge tone={PRI[t.priorita]}>{t.priorita}</Badge>{checkbox(t)}</div>}
                    onClick={() => navigate(`/task?open=${t.id}`)} />
                ))}
          </Section>

          {prossimi.length > 0 && (
            <Section title="In arrivo — prossimi 7 giorni" count={`${prossimi.length}`}>
              {prossimi.slice(0, 8).map((s, i) => (
                <Row key={s.id || i} icon={Clock} iconColor="var(--text-dim)"
                  title={s.titolo} sub={s.azienda || s.cliente_nome}
                  right={<span style={{ fontSize:11.5, color:'var(--text-dim)' }}>{s.data_scadenza ? new Date(s.data_scadenza+'T00:00:00').toLocaleDateString('it-IT',{day:'numeric',month:'short'}) : ''}</span>}
                  onClick={() => navigate('/scadenzario')} />
              ))}
            </Section>
          )}

          {completateOggi.length > 0 && (
            <Section title="Completate oggi" count={`${completateOggi.length}`} accent="var(--green)">
              {completateOggi.map(t => (
                <Row key={t.id} icon={CheckCircle2} iconColor="var(--green)"
                  title={<span style={{ textDecoration:'line-through', color:'var(--text-dim)' }}>{t.titolo}</span>}
                  sub={t.cliente_nome} right={checkbox(t)}
                  onClick={() => navigate(`/task?open=${t.id}`)} />
              ))}
            </Section>
          )}
        </>
      )}
    </div>
  );
}
